import React, { useState, useCallback } from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import SimpleAggregation from 'components/AggregationCards/SimpleAggregation';
import AppBar from 'components/AppBar';
import Button from 'components/Buttons/Button';
import MaterialInput from 'components/MaterialInput';
import ProgressSteps from 'components/ProgressSteps';
import { Desktop, Mobile } from 'components/Responsive';
import UserOnboardingCard from 'components/UserOnboardingCard';
import { WizardProps } from 'components/Wizard';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { formatTrim } from 'utils/generic';

import {
  Container,
  TransitionContainer,
  Header,
  PaddingContainer,
  BottomBackground,
  BottomBackgroundDesktop,
  FragmentContainer,
  Subtitle,
  StyledForm,
  GroupButton,
  UsersContainer,
  ButtonNext,
  InfoContainer,
  AggregationIcon,
} from '../../styles';

interface Props {
  wizard: WizardProps;
}

const UsersOnboarding = ({ wizard }: Props) => {
  const [isEdit, setIsEdit] = useState(false);
  const [userEdit, setUserEdit] = useState({ email: '' });
  const [isFocus, setIsFocus] = useState(false);
  const [isAggregate, setIsAggregate] = useState(false);
  const [isSkip, setIsSkip] = useState(() => {
    if (wizard.data.users.length >= 1) {
      return true;
    }
    return false;
  });

  const toast = useToast();
  const { t } = useStoreTranslation();
  const numberUsers = 3;

  const initialValues = {
    email: '',
    name: '',
    lastName: '',
  };

  const handleSubmit = useCallback(
    (data, { resetForm }) => {
      const { users } = wizard.data;

      if (users.some((user) => formatTrim(user.email) === formatTrim(data.email))) {
        toast.add({ type: 'danger', message: t('usersOnboardingForm.emailAlreadyAdd') });
        return;
      }

      setIsFocus(false);
      resetForm({});
      users.push(data);
      wizard.setData({
        ...wizard.data,
        users,
      });
    },
    [wizard, t, toast],
  );

  const toogleEdit = useCallback(
    (email: string) => {
      setIsEdit(true);
      setUserEdit(wizard.data.users.find((user) => formatTrim(user.email) === formatTrim(email)));
    },
    [wizard],
  );

  const handleEdit = useCallback(
    (data) => {
      const { users } = wizard.data;

      if (
        users
          .filter((user) => formatTrim(user.email) !== formatTrim(userEdit.email))
          .some((user) => formatTrim(user.email) === formatTrim(data.email))
      ) {
        toast.add({ type: 'danger', message: t('usersOnboardingForm.emailAlreadyAdd') });
        return;
      }

      const index = users.findIndex((user) => formatTrim(user.email) === formatTrim(userEdit.email));
      users[index] = data;
      wizard.setData({
        ...wizard.data,
        users,
      });
      setIsEdit(false);
      setUserEdit({ email: '' });
    },
    [wizard, userEdit, t, toast],
  );

  const validationSchema = yup.object().shape({
    email: yup.string().email().noPlusSign().required().max(100),
    name: yup.string().trim().required().max(100),
    lastName: yup.string().trim().required().max(100),
  });

  const setFocus = useCallback(() => {
    setIsFocus(true);
  }, []);

  const setAggregate = useCallback(() => {
    setIsAggregate(true);
  }, []);

  const renderMainContainer = () => {
    if (isEdit) {
      return (
        <FragmentContainer key="edit-state">
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={userEdit}
            validationSchema={validationSchema}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            onSubmit={handleEdit}
          >
            {({ isValid }: FormikValues) => (
              <>
                <StyledForm>
                  <PaddingContainer>
                    <Header>
                      <h3>{t('usersOnboardingForm.edit')}</h3>
                    </Header>

                    <MaterialInput
                      name="email"
                      onFocus={setFocus}
                      label={t('usersOnboardingForm.email')}
                      type="email"
                    />

                    <MaterialInput name="name" onFocus={setFocus} label={t('usersOnboardingForm.name')} />

                    <MaterialInput name="lastName" onFocus={setFocus} label={t('usersOnboardingForm.lastName')} />
                  </PaddingContainer>
                  <GroupButton>
                    <Button disabled={!isValid} value={t('usersOnboardingForm.saveButton')} />
                  </GroupButton>
                </StyledForm>
              </>
            )}
          </Formik>
        </FragmentContainer>
      );
    }
    if (isAggregate && wizard.data.users.length < numberUsers && !isSkip) {
      return (
        <FragmentContainer key="second-state">
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initialValues}
            validationSchema={validationSchema}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            onSubmit={handleSubmit}
          >
            {({ isValid }: FormikValues) => (
              <>
                <StyledForm>
                  <PaddingContainer>
                    <Header>
                      <h3>
                        {t('usersOnboardingForm.headerAfterChange', { number: numberUsers - wizard.data.users.length })}
                      </h3>
                    </Header>

                    <UsersContainer>
                      {wizard.data.users.map((u, index: number) => (
                        <UserOnboardingCard
                          key={formatTrim(u.email)}
                          email={u.email}
                          lastName={u.lastName}
                          name={u.name}
                          isCurrent={index + 1 === wizard.data.users.length}
                          isOpen={!isFocus && index + 1 === wizard.data.users.length}
                        />
                      ))}
                    </UsersContainer>

                    <MaterialInput
                      name="email"
                      onFocus={setFocus}
                      label={t('usersOnboardingForm.email')}
                      type="email"
                    />

                    <MaterialInput name="name" onFocus={setFocus} label={t('usersOnboardingForm.name')} />

                    <MaterialInput name="lastName" onFocus={setFocus} label={t('usersOnboardingForm.lastName')} />
                    {!isFocus && wizard.data.users.length < 1 && (
                      <InfoContainer type="leftTop">
                        <p>{t('usersOnboardingForm.infoInitialState')}</p>
                      </InfoContainer>
                    )}
                  </PaddingContainer>
                  <GroupButton>
                    {wizard.data.users.length >= 1 && (
                      <ButtonNext
                        type="button"
                        onClick={() => {
                          setIsSkip(true);
                        }}
                        disabled={false}
                        value={t('usersOnboardingForm.skip')}
                      />
                    )}
                    <Button disabled={!isValid} value={t('usersOnboardingForm.aggregateButton')} />
                  </GroupButton>
                </StyledForm>
              </>
            )}
          </Formik>
        </FragmentContainer>
      );
    }
    if ((isAggregate && wizard.data.users.length >= numberUsers) || isSkip) {
      return (
        <FragmentContainer key="final-state">
          <PaddingContainer>
            <Header>
              <h3>{t('usersOnboardingForm.headerFinalState')}</h3>
              <Subtitle>{t('usersOnboardingForm.subtitleFinalState')}</Subtitle>
            </Header>

            <UsersContainer>
              {wizard.data.users.map((u) => (
                <UserOnboardingCard
                  key={formatTrim(u.email)}
                  email={u.email}
                  lastName={u.lastName}
                  name={u.name}
                  isCurrent={false}
                  isOpen
                  isEdit
                  onClickOpen={toogleEdit}
                />
              ))}
            </UsersContainer>
            <InfoContainer type="leftTop">
              <p>{t('usersOnboardingForm.infoFinalState')}</p>
            </InfoContainer>
          </PaddingContainer>
          <GroupButton>
            <Button
              onClick={() => {
                wizard.nextStep();
              }}
              disabled={false}
              value={t('usersOnboardingForm.nextButton')}
            />
          </GroupButton>
        </FragmentContainer>
      );
    }
    return (
      <FragmentContainer key="default-state">
        <PaddingContainer>
          <Header>
            <h3>{t('usersOnboardingForm.header')}</h3>
            <Subtitle>{t('usersOnboardingForm.subtitle')}</Subtitle>
          </Header>
          <SimpleAggregation
            icon="group"
            rightIcon={<AggregationIcon />}
            placeholder={t('usersOnboardingForm.aggregate')}
            onClick={setAggregate}
          />
        </PaddingContainer>

        <Desktop>
          <BottomBackgroundDesktop>
            <BottomBackgroundDesktop.Description>
              {t('usersOnboardingForm.bottomDescription')}
            </BottomBackgroundDesktop.Description>
            <BottomBackgroundDesktop.Image>
              <img alt="onboarding-users" src="/corporate/images/onboarding_users.svg" />
            </BottomBackgroundDesktop.Image>
          </BottomBackgroundDesktop>
        </Desktop>
        <Mobile>
          <BottomBackground>
            <BottomBackground.Image>
              <img alt="onboarding-users" src="/corporate/images/onboarding_users.svg" />
            </BottomBackground.Image>
            <BottomBackground.Description>{t('usersOnboardingForm.bottomDescription')}</BottomBackground.Description>
          </BottomBackground>
        </Mobile>
      </FragmentContainer>
    );
  };

  return (
    <Container>
      <nav>
        <AppBar goBack={() => wizard.previousStep()}>{t('usersOnboardingForm.title')}</AppBar>
        <ProgressSteps size={wizard.size} currentStep={wizard.currentStep} />
      </nav>
      <TransitionContainer>
        <ReactCSSTransitionGroup transitionName="fade">{renderMainContainer()}</ReactCSSTransitionGroup>
      </TransitionContainer>
    </Container>
  );
};

UsersOnboarding.defaultProps = {
  wizard: {},
};

export default UsersOnboarding;
